import {type AutocompleteState, createAutocomplete} from '@algolia/autocomplete-core';
import {getAlgoliaResults} from '@algolia/autocomplete-preset-algolia';
import type {FacetFilters, Hit} from "algoliasearch";
import {
    type BaseSyntheticEvent,
    createContext,
    type Dispatch,
    type FC,
    type  KeyboardEvent,
    type  MouseEvent,
    type ReactNode,
    type RefObject,
    type SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useConfigContext} from "../../ConfigProvider.tsx";
import {useQuerySuggestions} from '../hooks/useQuerySuggestions';

type AutocompleteCoreState = AutocompleteState<Hit<AlgoliaProductAttributes>>;

interface AutocompleteContextProps {
    autocompleteState: AutocompleteCoreState;
    setAutocompleteState: Dispatch<SetStateAction<AutocompleteCoreState>>;
    setFilters: Dispatch<SetStateAction<string>>;
    setFacetFilters: Dispatch<SetStateAction<FacetFilters>>;
    autocomplete: ReturnType<typeof createAutocomplete<Hit<AlgoliaProductAttributes>, BaseSyntheticEvent, MouseEvent, KeyboardEvent>>;
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    inputRef: RefObject<HTMLInputElement | null>
    formRef: RefObject<HTMLFormElement | null>
    panelRef: RefObject<HTMLDivElement | null>
    searchRef: RefObject<HTMLDivElement | null>
    totalHits?: number
}

const AutocompleteContext = createContext<AutocompleteContextProps | undefined>(undefined);

export const AutocompleteProvider: FC<{ children: ReactNode }> = ({children}) => {
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [filters, setFilters] = useState<string>("");
    const [facetFilters, setFacetFilters] = useState<FacetFilters>("");
    const [totalHits] = useState<number>();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const {
        tc,
        searchClient,
        algoliaConfig: {algoliaIndexName, customizedUrl, autocompleteProductHits}
    } = useConfigContext();

    const [autocompleteState, setAutocompleteState] = useState<AutocompleteCoreState>({
        query: '',
        collections: [],
        isOpen: false,
        activeItemId: null,
        completion: null,
        context: {},
        status: 'idle'
    });

    const querySuggestionsInCategory = useQuerySuggestions();
    const filtersRef = useRef(filters);
    const facetFiltersRef = useRef(facetFilters);
    useEffect(() => {
        filtersRef.current = filters;
        autocomplete.refresh().then();
    }, [filters]);

    useEffect(() => {
        facetFiltersRef.current = facetFilters;
        autocomplete.refresh().then();
    }, [facetFilters]);
    const autocomplete = useMemo(
        () =>
            createAutocomplete<Hit<AlgoliaProductAttributes>, BaseSyntheticEvent, MouseEvent, KeyboardEvent>({
                onSubmit({state}) {
                    window.location.href = `/${customizedUrl}/search/?input_search=${state.query}`;
                },
                onStateChange({state}) {
                    setAutocompleteState(state);
                    //    handleStateChange(state);
                },
                insights: true,
                placeholder: tc.placeholder,
                openOnFocus: true,
                getSources({query}) {
                    if (!query) return [];
                    return [
                        {
                            sourceId: 'products',
                            getItems() {
                                return getAlgoliaResults({
                                    searchClient,
                                    queries: [
                                        {
                                            indexName: algoliaIndexName,
                                            params: {
                                                query,
                                                filters: filtersRef.current,
                                                facetFilters: facetFiltersRef.current,
                                                hitsPerPage: parseInt(autocompleteProductHits, 10),
                                            },
                                        },
                                    ]
                                })
                            },
                        },
                        /* {
                             sourceId: 'suggestions',
                             getItems({query}) {
                                 return getAlgoliaResults({
                                     searchClient,
                                     queries: [
                                         {
                                             indexName: algoliaIndexName,
                                             params: {
                                                 query,
                                                 filters: filtersRef.current,
                                                 facetFilters: facetFiltersRef.current,
                                                 hitsPerPage: parseInt(autocompleteSuggestionsHits, 10),
                                             },
                                         },
                                     ],
                                 });
                             },
                         },*/
                        // @ts-ignore
                        ...querySuggestionsInCategory.getSources({state: autocompleteState}),
                    ];
                },
            }),
        []
    );
    const {getEnvironmentProps} = autocomplete;

    useEffect(() => {
        if (!formRef.current || !panelRef.current || !inputRef.current) {
            return undefined;
        }
        const {onTouchStart, onTouchMove, onMouseDown} = getEnvironmentProps({
            formElement: formRef.current,
            inputElement: inputRef.current,
            panelElement: panelRef.current,
        });
        window.addEventListener('mousedown', onMouseDown);
        window.addEventListener('touchstart', onTouchStart);
        window.addEventListener('touchmove', onTouchMove);
        return () => {
            window.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
        };
    }, [getEnvironmentProps, autocompleteState.isOpen]);
    return (
        <AutocompleteContext.Provider value={{
            searchRef,
            panelRef,
            formRef,
            inputRef,
            autocompleteState,
            setAutocompleteState,
            autocomplete,
            setFilters,
            setFacetFilters,
            setIsModalOpen,
            isModalOpen,
            totalHits,

        }}>
            {children}
        </AutocompleteContext.Provider>
    );
};

export const useAutocomplete = () => {
    const context = useContext(AutocompleteContext);
    if (!context) {
        throw new Error('useAutocomplete must be used within an AutocompleteProvider');
    }
    return context;
};

